import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';

import QuestionDisplayContainer from '../question/QuestionDisplay';
import SectionQuestionsDisplayContainer from '../sectionview/SectionQuestionsDisplay';
import StimulusDisplay from '../stimulusDisplay/StimulusDisplay';

import assessmentReader from '../../store/readHelpers/AssessmentReader';
import i18nService, { t } from '../../services/i18nService';
import LoadTracker from '../../services/LoadTracker';
import marketplaceSettingsService from '../../services/MarketplaceSettingsService';
import viewReadyTracker from '../../services/ViewReadyTracker';

import styles from './DisplayTypeContainer.scss';
import utilService from '../../services/UtilService';
import SectionRubricBlock from '../../themes/components/fjord/SectionRubricBlock';
import dragAdjustmentService from '../../services/DragAdjustmentService';
import UIConstants from '../../constants/UIConstants';
import AnswerKeys from '../answerKeys/AnswerKeys';
import QuestionRubricBlockWithTitle from '../question/QuestionRubricBlockWithTitle';
import displayTypeContainerSelector from '../../selectors/displayTypeWrapper/DisplayTypeContainerSelector';
import sessionReader from '../../store/readHelpers/SessionReader';
import storeManager from '../../store/StoreManager';
import uiActions from '../../actions/UIActions';
import uiService from '../../services/UIService';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import sectionReader from '../../store/readHelpers/SectionReader';
import stimulusUiService from '../../services/StimulusUiService';
import AudioScriptOverlay from '../question/interactions/audioScript/AudioScriptOverlay';
import CscbQuestionInfo from '../question/interactions/cscb/CscbQuestionInfo';
import questionSelectionService from '../../services/QuestionSelectionService';
import assessmentActions from '../../actions/AssessmentActions';
import objectTypes from '../../constants/ObjectTypes';
import questionReader from '../../store/readHelpers/QuestionReader';
import SessionConstants from '../../constants/SessionConstants';
import a11yIIBScreenReaderService from '../../services/a11yIIBScreenReaderService';
import footerSelector from '../../selectors/footer/FooterSelector';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import settingsReader from '../../store/readHelpers/SettingsReader';

let windowResizeTimeout;

const isSubmissionTab = session =>
    session?.context === SessionConstants.CONTEXT_VIEW_GRADING_QUESTION_DELIVERY;

const isCbStimulusSubmissionTabEnabled = session =>
    marketplacePropertiesService.isEnableCbStimulusSubmissionTab() && isSubmissionTab(session);

export class DisplayTypeWrapper extends React.Component {
    static propTypes = {
        currentlyShown: PropTypes.object,
        currentQuestion: PropTypes.object,
        isQuestionPreviewMode: PropTypes.bool.isRequired,
        isSplitQuestionView: PropTypes.bool.isRequired,
        isStimulusResizingAllowed: PropTypes.bool.isRequired,
        isTopStimulus: PropTypes.bool.isRequired,
        regionAccessibilityAttributes: PropTypes.object.isRequired,
        response: PropTypes.object.isRequired,
        section: PropTypes.object,
        session: PropTypes.object.isRequired,
        settings: PropTypes.object.isRequired,
        shouldAddSectionAriaAttributes: PropTypes.bool.isRequired,
        shouldAdjustSectionPaddings: PropTypes.bool.isRequired,
        shouldRenderAnswerKeys: PropTypes.bool.isRequired,
        showQuestionCopyrightText: PropTypes.bool.isRequired,
        showQuestionRubricBlockOnSectionLevel: PropTypes.bool.isRequired,
        showQuestionRubricBlockOnTopLevel: PropTypes.bool.isRequired,
        showSectionRubricBlockOnSectionLevel: PropTypes.bool.isRequired,
        showSectionRubricBlockOnTopLevel: PropTypes.bool.isRequired,
        showStimulus: PropTypes.bool.isRequired,
        showUpperLevelQuestionRubricBlock: PropTypes.bool.isRequired,
        stimulusContent: PropTypes.object,
        stimulusHolder: PropTypes.string,
        stimulusPosition: PropTypes.string,
        stimulusSize: PropTypes.number,
        ui: PropTypes.object,
        verticalDisplay: PropTypes.bool.isRequired,
    };

    constructor(props) {
        super(props);
        const { section, session } = this.props;

        this.isSubmissionTabStimulusEnabled = isCbStimulusSubmissionTabEnabled(session);
        this.isSubmissionTabContext = isSubmissionTab(session);
        this.isTouchDevice = utilService.isTouchDevice();
        this.isCSCB = questionSelectionService.isCandidateSelectedContentBlockEnabledForSection(section?.id);

        this.initializeStimulusProperties();
        this.addTouchListeners();

        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.dragStop = this.dragStop.bind(this);
        this.state = {
            stimulusSize: this.getInitialStimulusSize(),
            containerStyle: {},
            dragging: false,
        };
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.reportReady = viewReadyTracker.getReporter();
        this.initLoadTracker(this.props.verticalDisplay, this.props.showStimulus);
    }

    initializeStimulusProperties() {
        const { section, showStimulus, stimulusPosition, isTopStimulus } = this.props;
        this.stimulusPosition = (this.isSubmissionTabStimulusEnabled && !isTopStimulus) ? UIConstants.STIMULUS_POSITION.LEFT : stimulusPosition;
        this.inlineStimulus = isTopStimulus && !marketplaceSettingsService.useFixedTopStimulus();
        if (showStimulus) {
            if (this.isSubmissionTabStimulusEnabled && !isTopStimulus) {
                this.startStimulusSize = UIConstants.DEFAULT_STIMULUS_SIZE.STIMULUS_SIZE;
            } else {
                const persistedStimulusSize = this.getPersistedStimulusSize();
                this.startStimulusSize = Number.isFinite(persistedStimulusSize) ? persistedStimulusSize : this.getStartStimulusSize(section);
            }
        }
    }

    getInitialStimulusSize() {
        const { showStimulus, isTopStimulus } = this.props;
        if (!showStimulus) {
            return undefined;
        }

        const persistedStimulusSize = this.getPersistedStimulusSize();
        const hasPersistedStimulusSize = Number.isFinite(persistedStimulusSize);

        if (this.isSubmissionTabStimulusEnabled && !isTopStimulus) {
            const stimulusWidthBasis = hasPersistedStimulusSize ? this.getMainScreenContentWidth() : window.innerWidth;
            const leftStimulusWidth = this.getLeftStimulusWidth(
                stimulusWidthBasis,
                this.startStimulusSize,
                !hasPersistedStimulusSize
            );
            return Math.max(2, Math.min(leftStimulusWidth, window.innerWidth - 36));
        }

        if (isTopStimulus) {
            return hasPersistedStimulusSize
                ? Math.max(UIConstants.DEFAULT_STIMULUS_SIZE.MIN_STIMULUS_HEIGHT, Math.min(persistedStimulusSize, window.innerHeight))
                : (window.innerHeight / 2) - 60;
        }

        const width = this.getMainScreenContentWidth();
        const stimulusWidthBasis = hasPersistedStimulusSize ? width : window.innerWidth;
        const leftStimulusWidth = this.getLeftStimulusWidth(
            stimulusWidthBasis,
            this.startStimulusSize,
            !hasPersistedStimulusSize
        );
        return Math.max(2, Math.min(leftStimulusWidth, window.innerWidth - 36));
    }

    addTouchListeners() {
        // The following prevents scrolling on resizing stimulus
        this.scrollable = true;
        this.listener = (e) => {
            if (!this.scrollable) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', this.listener, { passive: false });
    }

    snapSectionRight = () => {
        const scrollableElement = document.getElementById('sectionContent');

        if (scrollableElement) {
            scrollableElement.scrollLeft = 0;
        }
    };

    componentDidMount() {
        const { showStimulus } = this.props;
        if (showStimulus && !this.inlineStimulus) {
            this.setStimulusSize();
        }
        window.addEventListener('resize', this.handleWindowResize);

        const { section } = this.props;
        if (assessmentReader.hasMatchQuestions(section)) {
            dragAdjustmentService.init();
        }

        setTimeout(() => {
            this.calculateContainerStyle();
        }, 50);
        this.handleStimulusUpdate();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        const { currentlyShown, section, showStimulus } = this.props;
        const sectionChanged = section.id !== nextProps.section.id;
        const currentShownSectionChanged = currentlyShown.sectionId !== nextProps.currentlyShown.sectionId;
        const currentShownQuestionChanged = currentlyShown.questionId !== nextProps.currentlyShown.questionId;
        const showStimulusChanged = showStimulus !== nextProps.showStimulus;
        const stimulusPositionChanged = this.props.stimulusPosition !== nextProps.stimulusPosition;
        const topStimulusChanged = this.props.isTopStimulus !== nextProps.isTopStimulus;

        if (nextProps.section && (currentShownSectionChanged || currentShownQuestionChanged)) {
            this.reportReady = viewReadyTracker.getReporter();
            const isStimulusContentUpdated = nextProps.showStimulus && (sectionChanged || !showStimulus);
            this.initLoadTracker(nextProps.verticalDisplay, isStimulusContentUpdated);
        }
        if ((nextProps.section && sectionChanged)
            || showStimulusChanged
            || currentShownQuestionChanged
            || stimulusPositionChanged
            || topStimulusChanged) {
            this.conditionallySetStimulusSize(nextProps);
        }
    }

    componentDidUpdate(prevProps) {
        const { section } = this.props;
        const contentUpdated = prevProps.section?.questions !== section?.questions ||
            prevProps.section?.stimulusContent !== section?.stimulusContent;

        if (contentUpdated) {
            if (assessmentReader.hasMatchQuestions(section)) {
                dragAdjustmentService.init();
            } else {
                dragAdjustmentService.destroy();
            }
        }

        const isSidebarToggled = (prevProps.session.hypothesisSidebarOpen !== this.props.session.hypothesisSidebarOpen)
            || (prevProps.session.annotationsSidebarOpen !== this.props.session.annotationsSidebarOpen);

        if (isSidebarToggled) {
            this.setStimulusSize();
        }

        if ((prevProps.showStimulus !== this.props.showStimulus)
            || (prevProps.isSplitQuestionView !== this.props.isSplitQuestionView)
            || isSidebarToggled) {
            setTimeout(() => {
                this.calculateContainerStyle();
            }, 50);
        }
        const hasContentBlockChanged = prevProps?.currentQuestion?.nestedSectionId !== this.props?.currentQuestion?.nestedSectionId;
        if (hasContentBlockChanged) {
            this.handleStimulusUpdate();
        }
    }

    handleStimulusUpdate = () => {
        const { section: originalSection, currentQuestion } = this.props;

        if (
            !originalSection ||
            !currentQuestion ||
            !questionSelectionService.isCandidateSelectedContentBlockEnabled()
        ) {
            return;
        }

        const { sections = [], questions = [], initialStimulus } = originalSection;

        if (originalSection.id === originalSection?.stimulusContent?.parentId) return;

        const correctStimulus = questions.find(
            q =>
                q.category === objectTypes.CATEGORY.STIMULUS &&
                q.nestedSectionId === currentQuestion?.nestedSectionId
        );

        let sectionUpdatePayload = { id: originalSection.id };

        if (correctStimulus) {
            const matchedNestedSection = this.findSectionByStimulusId(sections, correctStimulus.contentRevisionId);

            if (matchedNestedSection) {
                sectionUpdatePayload = {
                    ...sectionUpdatePayload,
                    stimulusContent: correctStimulus,
                    allowStimulusResizing: matchedNestedSection.allowStimulusResizing,
                    layout: matchedNestedSection.layout,
                    stimulusSize: matchedNestedSection.stimulusSize,
                };
                this.startStimulusSize = matchedNestedSection.stimulusSize;
                storeManager.dispatch(assessmentActions.updateSection(sectionUpdatePayload));
            }
        } else {
            const matchedNestedSection = this.findSectionByStimulusId(sections, initialStimulus?.contentRevisionId);

            if (matchedNestedSection) {
                sectionUpdatePayload = {
                    ...sectionUpdatePayload,
                    stimulusContent: initialStimulus,
                    allowStimulusResizing: initialStimulus.allowStimulusResizing,
                    layout: initialStimulus.layout,
                    stimulusSize: initialStimulus.stimulusSize,
                };
                this.startStimulusSize = initialStimulus.stimulusSize;
            } else {
                sectionUpdatePayload = {
                    ...sectionUpdatePayload,
                    stimulusContent: undefined,
                    allowStimulusResizing: undefined,
                    layout: undefined,
                    stimulusSize: undefined,
                };
            }

            storeManager.dispatch(assessmentActions.updateSection(sectionUpdatePayload));
        }
    };

    findSectionByStimulusId = (sections, stimulusId) =>
        sections.find(section =>
            section.questions?.some(q => q.contentRevisionId === stimulusId)
        );


    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
        if (this.listener) {
            document.removeEventListener('touchmove', this.listener, { passive: false });
        }
        clearTimeout(windowResizeTimeout);
        windowResizeTimeout = undefined;
        dragAdjustmentService.destroy();
    }

    getLeftStimulusWidth = (width, stimulusSize, useLegacyPaddingCompensation = false) => {
        if (!stimulusSize) {
            return 0;
        }
        const calculatedWidth = width / (100 / stimulusSize);
        return Math.max(0, useLegacyPaddingCompensation ? calculatedWidth - 32 : calculatedWidth);
    };

    getStartStimulusSize = (section) => {
        const { DEFAULT_STIMULUS_SIZE = 50 } = window.marketplaceProperties;
        if (isNaN(section.stimulusSize)) {
            return DEFAULT_STIMULUS_SIZE;
        }
        return section.stimulusSize;
    };

    getPersistedStimulusSize = (props = this.props) => {
        const persistedStimulusSize = uiService.isTopStimulusPosition(props.stimulusPosition)
            ? props.ui?.topStimulusSize
            : props.stimulusSize;

        return (this.isSubmissionTabStimulusEnabled
            || !props.isStimulusResizingAllowed
            || !Number.isFinite(persistedStimulusSize)
            ? undefined
            : persistedStimulusSize);
    };

    handleWindowResize() {
        clearTimeout(windowResizeTimeout);
        windowResizeTimeout = undefined;
        windowResizeTimeout = setTimeout(() => {
            if (this.props.showStimulus && !this.inlineStimulus) {
                this.conditionallySetStimulusSize(this.props);
            }
            this.calculateContainerStyle();
        }, 50);
    }

    conditionallySetStimulusSize = (props) => {
        const { showStimulus, stimulusPosition, isTopStimulus, section } = props;

        if (!showStimulus) {
            return;
        }

        this.stimulusPosition = (this.isSubmissionTabStimulusEnabled && !isTopStimulus) ? UIConstants.STIMULUS_POSITION.LEFT : stimulusPosition;
        this.inlineStimulus = isTopStimulus && !marketplaceSettingsService.useFixedTopStimulus();
        if (this.inlineStimulus) {
            return;
        }

        const baselineStimulusSize = (this.isSubmissionTabStimulusEnabled && !isTopStimulus)
            ? UIConstants.DEFAULT_STIMULUS_SIZE.STIMULUS_SIZE
            : this.getStartStimulusSize(section);
        const persistedStimulusSize = this.getPersistedStimulusSize(props);
        this.startStimulusSize = Number.isFinite(persistedStimulusSize) ? persistedStimulusSize : baselineStimulusSize;
        this.setStimulusSize();
    }

    getHypothesisSidebarSize = () => {
        const sidebar = document.querySelector('hypothesis-sidebar');
        const shadow = sidebar?.shadowRoot;
        if (shadow) {
            const hypothesis = shadow.querySelector('iframe.h-sidebar-iframe');
            if (hypothesis) {
                return hypothesis.contentWindow.innerWidth;
            }
        }
        // Fallback for when the sidebar is not in shadow DOM
        const hypothesis = document.querySelector('.h-sidebar-iframe');
        if (hypothesis) {
            return hypothesis.contentWindow.innerWidth;
        }
        return 0;
    };

    setStimulusSize = () => {
        const persistedStimulusSize = this.getPersistedStimulusSize();
        const hasPersistedStimulusSize = Number.isFinite(persistedStimulusSize);

        if (this.stimulusPosition === 'top') {
            if (hasPersistedStimulusSize) {
                this.setStimulusSizeState();
                return;
            }
            this.setState({
                stimulusSize: undefined,
            }, () => {
                setTimeout(() => {
                    this.setStimulusSizeState();
                }, 50);
            });
        } else {
            setTimeout(() => {
                this.setStimulusSizeState();
            }, 100);
        }
    };

    setStimulusSizeState = () => {
        const stimulusSize = this.calculateStimulusSize();
        if (stimulusSize !== undefined) {
            this.setState({
                stimulusSize,
            });
        }
    };

    calculateStimulusSize = () => {
        const persistedStimulusSize = this.getPersistedStimulusSize();
        const hasPersistedStimulusSize = Number.isFinite(persistedStimulusSize);

        if (this.stimulusPosition === 'top') {
            if (hasPersistedStimulusSize) {
                const minHeight = UIConstants.DEFAULT_STIMULUS_SIZE.MIN_STIMULUS_HEIGHT;
                const maxHeight = window.innerHeight;
                return Math.min(Math.max(persistedStimulusSize, minHeight), maxHeight);
            }
            if (!this.sectionStimulus?.stimulus) {
                return undefined;
            }
            const { marginTop, marginBottom } = getComputedStyle(this.sectionStimulus.stimulus);
            const stimulusMarginTop = parseInt(marginTop, 10);
            const stimulusMarginBottom = parseInt(marginBottom, 10);
            const stimulusHeight = this.sectionStimulus.stimulus.clientHeight;
            const totalHeight = stimulusMarginTop + stimulusMarginBottom + stimulusHeight;
            if (totalHeight < (window.innerHeight / 2) - 60) {
                return totalHeight;
            }
            return (window.innerHeight / 2) - 58; // 58px is the height of the footer
        }

        const width = this.getMainScreenContentWidth();
        const stimulusSizeValue = hasPersistedStimulusSize ? persistedStimulusSize : this.startStimulusSize;

        const leftStimulusWidth = this.getLeftStimulusWidth(width, stimulusSizeValue, !hasPersistedStimulusSize);
        return Math.max(2, Math.min(leftStimulusWidth, window.innerWidth - 36));
    };

    calculateContainerStyle = () => {
        const {
            showStimulus,
            isSplitQuestionView,
            session,
            currentQuestion,
        } = this.props;

        const containerStyle = {};

        const isGapMatchQuestionWithMultipleInteractions = questionReader.isGapMatchQuestionWithMultipleInteractions(currentQuestion);

        if (!isGapMatchQuestionWithMultipleInteractions && (showStimulus || isSplitQuestionView)) {
            const offsetHeightSum = this.calculateOffsetHeight();
            // dynamically calculate max height for container depends on offestHeightSum
            containerStyle.maxHeight = `calc(100vh - ${offsetHeightSum}px)`;
        }

        if (isGapMatchQuestionWithMultipleInteractions) {
            containerStyle.height = 'fit-content';
        }

        if (session.hypothesisSidebarOpen) {
            containerStyle.width = `${this.calculateContainerWidth()}px`;
        }

        if (!utilService.isDeepEqual(containerStyle, this.state.containerStyle)) {
            this.setState({
                containerStyle,
            });
        }
    }

    getMainScreenContentFallbackWidth = () => {
        if (this.props.session.hypothesisSidebarOpen) {
            return Math.max(0, this.calculateContainerWidth());
        }

        return Math.max(0, window.innerWidth);
    };

    getMainScreenContentWidth = () => {
        const { session } = this.props;
        const mainScreenContent = document.getElementById('main-screen-content');

        if (session.hypothesisSidebarOpen) {
            return this.calculateContainerWidth();
        }

        return mainScreenContent ? mainScreenContent.offsetWidth : this.getMainScreenContentFallbackWidth();
    };

    calculateContainerWidth = () => {
        const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return window.innerWidth - this.getHypothesisSidebarSize() - (3 * rem);
    };

    initLoadTracker = (verticalDisplay, isStimulusContentUpdated = false) => {
        this.loadTracker = new LoadTracker((success) => {
            this.reportReady(success);
        }, 'DisplayTypeContainer');

        if (isStimulusContentUpdated) {
            this.stimulusTracker = this.loadTracker.addItemToTrack('StimulusDisplay');
        }
        const { section, currentQuestion } = this.props;
        if (verticalDisplay && sectionReader.shouldShowVericalDisplay(section, sessionReader, currentQuestion)) {
            this.sectionTracker = this.loadTracker.addItemToTrack('SectionQuestionsDisplayContainer');
        } else {
            this.questionTracker = this.loadTracker.addItemToTrack('QuestionDisplayContainer');
        }

        this.loadTracker.setAllItemsAdded();
    };

    handleDragAction = (e, data) => {
        const { stimulusSize } = this.state;
        const isTopStimulus = this.stimulusPosition === 'top';
        const offset = isTopStimulus ? data.deltaY : data.deltaX;
        this.scrollable = false;

        let newStimulusSize = stimulusSize + offset;
        if (!isTopStimulus && newStimulusSize < 2) {
            newStimulusSize = 2;
        } else if (isTopStimulus && newStimulusSize < UIConstants.DEFAULT_STIMULUS_SIZE.MIN_STIMULUS_HEIGHT) {
            newStimulusSize = UIConstants.DEFAULT_STIMULUS_SIZE.MIN_STIMULUS_HEIGHT;
        } else if (isTopStimulus && newStimulusSize > window.innerHeight) {
            newStimulusSize = window.innerHeight;
        } else if (newStimulusSize > (window.innerWidth - 36)) {
            newStimulusSize = window.innerWidth - 36;
        }
        this.setState({
            stimulusSize: newStimulusSize,
        });
    };

    dragStop = () => {
        this.scrollable = true;
        const isTopStimulus = this.stimulusPosition === 'top';

        if (this.isSubmissionTabStimulusEnabled) {
            storeManager.dispatch(uiActions.setStimulusState(UIConstants.STIMULUS_STATE.DROP));
            this.setState({
                dragging: false,
            });
            return;
        }

        if (isTopStimulus) {
            storeManager.dispatch(uiActions.setProperties({ topStimulusSize: this.state.stimulusSize }));
        } else {
            const width = this.getMainScreenContentWidth();
            const stimulusSize = (this.state.stimulusSize / width) * 100;
            storeManager.dispatch(uiActions.setStimulusSize(stimulusSize));
        }

        storeManager.dispatch(uiActions.setStimulusState(UIConstants.STIMULUS_STATE.DROP));
        this.setState({
            dragging: false,
        });
    };

    dragStart = () => {
        this.setState({
            dragging: true,
        });
        storeManager.dispatch(uiActions.setStimulusState(UIConstants.STIMULUS_STATE.DRAG));
    };

    calculateOffsetHeight = () => {
        const footerElement = footerSelector.isA11yV5AndAboveSupported()
            ? document.getElementById('playerFooter')
            : document.getElementsByTagName('footer')[0];
        const headerElement = document.getElementById('headerTopBar');
        const rubricTopLevel = document.getElementById('rubric__top-level');

        const offsetsArray = [
            footerElement?.offsetHeight,
            headerElement?.offsetHeight,
            rubricTopLevel?.offsetHeight,
        ];

        // 455 - this is the height that is applied for css media query (zoomL)
        // 72 - this is padding 4.5rem in css for zoomM and zoomL
        const defaultOffset = !sessionReader.isDataSetTypeQuestionSet()
            && document.body.clientHeight <= 455 ? -72 : 0;
        return offsetsArray.reduce((acc, offsetHeight) => {
            let summValue = acc;

            if (offsetHeight) {
                summValue += offsetHeight;
            }

            return summValue;
        }, defaultOffset);
    }

    getAriaLabel = () => {
        const {
            currentlyShown,
            section,
            stimulusHolder,
        } = this.props;

        return uiService.isQuestionStimulusHolder(stimulusHolder)
            ? scorableItemNumberingService.getQuestionTitle(currentlyShown.questionId)
            : `${t('generic.sectionNumberPrefix')} ${section.title}`;
    };

    render() {
        const {
            currentlyShown,
            currentQuestion,
            isQuestionPreviewMode,
            isSplitQuestionView,
            isStimulusResizingAllowed,
            regionAccessibilityAttributes,
            response,
            section,
            session,
            settings,
            shouldAddSectionAriaAttributes,
            shouldAdjustSectionPaddings,
            shouldRenderAnswerKeys,
            showQuestionCopyrightText,
            showQuestionRubricBlockOnSectionLevel,
            showQuestionRubricBlockOnTopLevel,
            showSectionRubricBlockOnSectionLevel,
            showSectionRubricBlockOnTopLevel,
            showStimulus,
            showUpperLevelQuestionRubricBlock,
            stimulusContent,
            stimulusHolder,
            ui,
            verticalDisplay,
        } = this.props;
        const { dragging, stimulusSize } = this.state;

        if (session?.context === SessionConstants.CONTEXT_PREVIEW_AUDIO_SCRIPT) {
            const assessment = storeManager.getState().assessment;

            const audioScriptResource = assessment?.sections?.[0]?.additionalResources?.find(
                res => res.objectType === objectTypes.AUDIO_SCRIPT
            );
            if (audioScriptResource) {
                const overlayModel = {
                    script: audioScriptResource.itemBody,
                };
                return <AudioScriptOverlay model={overlayModel} />;
            }
            return null;
        }


        if (shouldRenderAnswerKeys) {
            return <AnswerKeys />;
        }

        if (!section) {
            return null;
        }

        const shouldShowStimulus = showStimulus
            && (!this.isSubmissionTabContext || this.isSubmissionTabStimulusEnabled);

        const shouldHideResponseAreaInPreview =
            sessionReader.getLayoutParam(sessionReader.layoutParamNames.HIDE_RESPONSE_AREA, false) === true;

        const shouldHideResponseArea = settingsReader.shouldHideResponseAreasForPaperSubmissions(currentQuestion)
            || settingsReader.shouldHideResponseAreaInDemoTestForPaperSubmissions(currentQuestion);

        let stimulusStyle;
        const shouldTakeFullSpace = shouldHideResponseAreaInPreview || shouldHideResponseArea;
        if (shouldShowStimulus) {
            // Note: these flags represent different concerns but share the same layout behavior.
            // - shouldHideResponseAreaInPreview: Client preview where the response area is hidden.
            // - shouldHideResponseArea: paper-based submissions where the response area is hidden.
            // In both cases the stimulus should take the full available space.
            const sizeValue = shouldTakeFullSpace ? '100%' : stimulusSize;
            stimulusStyle = (this.isSubmissionTabStimulusEnabled || this.stimulusPosition !== 'top')
                ? { width: sizeValue } : { height: sizeValue };
        }

        const layoutClassName = this.isSubmissionTabStimulusEnabled
            ? 'left-stimulus'
            : stimulusUiService.layoutClassName(showStimulus, this.stimulusPosition);

        const sectionNumber = section.questions && section.questions.length ? section.questions[0].sectionNumber : null;

        const containerClassList = [styles.displayTypeContainer];
        if (shouldShowStimulus || isSplitQuestionView) {
            containerClassList.push(layoutClassName);
        }
        if (showUpperLevelQuestionRubricBlock) {
            containerClassList.push('has-section-level-dynamic-rubric');
        }
        if (isQuestionPreviewMode) {
            containerClassList.push(styles.questionPreview);
        }

        if (this.isCSCB) {
            containerClassList.push(styles.cscbQuestion);
        }

        if (shouldTakeFullSpace) {
            containerClassList.push('stimulus-only');
        }

        const sectionContentClassList = [styles.sectionContent, UIConstants.GAP_MATCH_SCROLL_CLASS];
        if (shouldHideResponseArea) {
            sectionContentClassList.push('hide-writing-content-area');
        }
        if (shouldAdjustSectionPaddings) {
            sectionContentClassList.push('contains-split-question-view');
        }
        const orientation = this.stimulusPosition === 'top' ? 'horizontal' : 'vertical';

        const stimulusQuestionSeparatorLabel = a11yIIBScreenReaderService.getAriaLabelValue(
            undefined,
            t('displayTypeContent.resizableSeparatorAriaLabel'));

        return (
            <React.Fragment>
                {
                    this.isCSCB && <CscbQuestionInfo
                        currentQuestion={currentQuestion}
                        section={section}
                        inlineSectionNumber={sectionNumber !== undefined ? sectionNumber : 0}
                    />
                }
                { showSectionRubricBlockOnTopLevel && !uiService.isPaperBasedRenderForCEQ() && <SectionRubricBlock section={section} ui={ui} /> }

                { showQuestionRubricBlockOnTopLevel &&
                    <div className={styles.upperLevelQuestionDynamicRubricBlock} id="rubric__top-level">
                        <QuestionRubricBlockWithTitle question={currentQuestion} section={section} />
                    </div>
                }

                <div className={containerClassList.join(' ')} style={this.state.containerStyle}>
                    { shouldShowStimulus && !this.inlineStimulus &&
                        <React.Fragment>
                            <StimulusDisplay
                                ref={(sectionStimulus) => { this.sectionStimulus = sectionStimulus; }}
                                currentlyShown={currentlyShown}
                                itemResult={response[stimulusContent.contentRevisionId]}
                                layoutClassName={layoutClassName}
                                onReady={this.stimulusTracker}
                                sectionId={section.id}
                                sectionNumber={sectionNumber}
                                sectionTitle={section.title}
                                session={session}
                                settings={settings}
                                stimulusContent={stimulusContent}
                                stimulusHolder={stimulusHolder}
                                style={stimulusStyle}
                                ui={ui}
                            />
                            { !shouldHideResponseAreaInPreview && isStimulusResizingAllowed &&
                                <Draggable
                                    axis="none"
                                    onDrag={this.handleDragAction}
                                    onStop={this.dragStop}
                                    onStart={this.dragStart}
                                >
                                    <div
                                        className={
                                            `${styles.divider} ${this.isTouchDevice ? ' touch-device' : ''}
                                            ${(this.isSubmissionTabStimulusEnabled || dragging) ? 'dragging' : ''}`
                                        }
                                    >
                                        <div className={styles.resizeBar}>
                                            <div
                                                className={styles.resizeBtn}
                                                tabIndex="0"
                                                role="separator"
                                                aria-orientation={orientation}
                                                aria-label={stimulusQuestionSeparatorLabel}
                                            >
                                                <i
                                                    className={`fa fa-arrows-${this.stimulusPosition === 'top' ? 'v' : 'h'}`}
                                                    aria-hidden="true"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </Draggable>
                            }
                        </React.Fragment>
                    }

                    { /* If flag true => DO NOT display user response area at all */ }
                    { !shouldHideResponseAreaInPreview &&
                        <div
                            className={sectionContentClassList.join(' ')}
                            id="sectionContent"
                            {...regionAccessibilityAttributes}
                        >
                            { shouldAddSectionAriaAttributes &&
                                <span className="sr-only">
                                    { this.getAriaLabel() }
                                </span>
                            }

                            { showStimulus && this.inlineStimulus &&
                                <StimulusDisplay
                                    currentlyShown={currentlyShown}
                                    itemResult={response[stimulusContent.contentRevisionId]}
                                    layoutClassName={layoutClassName}
                                    onReady={this.stimulusTracker}
                                    sectionId={section.id}
                                    sectionNumber={sectionNumber}
                                    session={session}
                                    settings={settings}
                                    stimulusContent={stimulusContent}
                                    stimulusHolder={stimulusHolder}
                                    ui={ui}
                                />
                            }

                            { showSectionRubricBlockOnSectionLevel && !uiService.isPaperBasedRenderForCEQ() &&
                                <SectionRubricBlock section={section} ui={ui} />
                            }

                            { showQuestionRubricBlockOnSectionLevel &&
                                <div className={styles.upperLevelQuestionDynamicRubricBlock}>
                                    <QuestionRubricBlockWithTitle question={currentQuestion} section={section} isSectionLevel />
                                </div>
                            }

                            { verticalDisplay && sectionReader.shouldShowVericalDisplay(section, sessionReader, currentQuestion)
                                ? <SectionQuestionsDisplayContainer
                                    onReady={this.sectionTracker}
                                    isQuestionRubricBlockShown={showUpperLevelQuestionRubricBlock}
                                />
                                : <QuestionDisplayContainer
                                    onReady={this.questionTracker}
                                    isQuestionRubricBlockShown={showUpperLevelQuestionRubricBlock}
                                    onContentLayoutReady={this.snapSectionRight}
                                />
                            }

                            { showQuestionCopyrightText && layoutClassName !== 'left-stimulus' &&
                                <div className={styles.copyright}>
                                    {i18nService.translate(`question.copyright.${ui.marketplaceTheme}`)}
                                </div>
                            }
                        </div>
                    }
                </div>
            </React.Fragment>
        );
    }
}

class ErrorBuffer extends React.Component {
    static propTypes = {
        error: PropTypes.bool,
    };

    render() {
        if (this.props.error) {
            return null;
        }

        return <DisplayTypeWrapper {...this.props} />;
    }
}

const mapStateToProps = (state) => {
    try {
        return displayTypeContainerSelector.selectProps(state);
    } catch (error) {
        window.console.warn('Entering DisplayTypeContainer with invalid data', error);

        return { error: true };
    }
};

const DisplayTypeContainer = connect(mapStateToProps)(ErrorBuffer);
export default DisplayTypeContainer;
